import { useCallback, useEffect, useRef, useState, useLayoutEffect } from "react";
import { broadcast } from "./picoBroadcastChannel.ts";

export interface WebSocketMessage {
  type: string;
  data?: unknown;
}

export interface UseWebSocketOptions {
  url: string;
  clientId: string;
  onMessage: (message: WebSocketMessage) => void;
  broadcastToOtherTabs?: boolean;
  enabled?: boolean;
}

export interface UseWebSocketReturn {
  connected: boolean;
  send: (message: unknown) => void;
}

const INITIAL_RECONNECT_MS = 500;
const MAX_RECONNECT_MS = 30000;

export function useWebSocket({
  url,
  clientId,
  onMessage,
  broadcastToOtherTabs = true,
  enabled = true,
}: UseWebSocketOptions): UseWebSocketReturn {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_MS);
  const closedByUserRef = useRef(false);
  const onMessageRef = useRef(onMessage);
  const [connected, setConnected] = useState(false);
  useLayoutEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (!enabled) return;
    closedByUserRef.current = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = (): void => {
      const fullUrl = `${url}?clientId=${encodeURIComponent(clientId)}`;
      const ws = new WebSocket(fullUrl);
      wsRef.current = ws;

      ws.addEventListener("open", () => {
        reconnectDelayRef.current = INITIAL_RECONNECT_MS;
        setConnected(true);
      });

      ws.addEventListener("message", (event) => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage;
          onMessageRef.current(message);
          if (broadcastToOtherTabs) {
            broadcast({ type: message.type, payload: message.data });
          }
        } catch (err) {
          console.error("[useWebSocket] invalid message:", err);
        }
      });

      ws.addEventListener("close", (event) => {
        wsRef.current = null;
        setConnected(false);
        if (closedByUserRef.current) return;
        console.warn(
          `[useWebSocket] closed (code=${event.code} reason="${event.reason}" wasClean=${event.wasClean}) — reconnect in ${reconnectDelayRef.current}ms`,
        );
        const delay = Math.min(reconnectDelayRef.current, MAX_RECONNECT_MS);
        reconnectTimer = setTimeout(connect, delay);
        reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, MAX_RECONNECT_MS);
      });

      ws.addEventListener("error", (event) => {
        console.warn("[useWebSocket] error event:", event);
        ws.close();
      });
    };

    connect();

    return () => {
      closedByUserRef.current = true;
      setConnected(false);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [url, clientId, broadcastToOtherTabs, enabled]);

  const send = useCallback((message: unknown) => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(message));
  }, []);

  return { connected, send };
}
